<template>
    <div class="escrow-invoice">
        <page-loader :loading="loading"/>

        <full-screen-dialog
            v-if="!loading"
            :header-back-button-disabled="navigationDisabled"
            header-close-button-disabled
        >
            <div v-if="order">
                <div class="text-center">
                    <b-img
                        class="logo"
                        :src="logo.path"
                        alt="Main Logo"
                    />
                </div>

                <div class="pdf-show">
                    <span class="invoice-title">Invoice</span>
                </div>
                <div class="pdf-hide">
                    <b-row>
                        <b-col cols="12">
                            <h1 class="text-center mb-5">Sellers Shield Invoice</h1>
                            <p class="red-subtitle pink-text">Please note: Sellers Shield is not a home warranty!</p>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="12">
                            <b-btn
                                variant="primary"
                                size="lg"
                                id="print-button"
                                class="download-btn text-center mt-5 mb-3"
                                @click="printInvoicePdf"
                            >
                                <i class="fas fa-print"></i> Download PDF
                            </b-btn>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="12">
                            <p class="order-info-title">Order information:</p>
                        </b-col>
                    </b-row>
                </div>
                <hr/>

                <div class="grey-col">
                    <b-row>
                        <b-col cols="4">Order #</b-col>
                        <b-col cols="8">
                            <span>{{ this.hslp.escrowInvoiceId }}</span>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col cols="4">Order property</b-col>
                        <b-col cols="8">
                            <span>{{ this.propertyData }}</span>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col cols="4">Home seller</b-col>
                        <b-col cols="8">
                            <span>{{ this.primarySeller }}</span>
                        </b-col>
                    </b-row>

                    <b-row
                        v-if="this.agent"
                    >
                        <b-col cols="4">Real estate agent</b-col>
                        <b-col cols="8">
                            <span>{{ this.agent.firstName }} {{ this.agent.lastName }}</span>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col cols="4">Closing date</b-col>
                        <b-col cols="8">
                            <span v-if="this.closingDate">{{ this.closingDate }}</span>
                            <span v-else>TBD</span>
                        </b-col>
                    </b-row>

                    <b-row v-if="this.broker">
                        <b-col cols="4">Brokerage</b-col>
                        <b-col cols="8">
                            <span>{{ this.broker.companyName }}</span>
                        </b-col>
                    </b-row>
                </div>
                <hr/>
                <b-row
                    class="table-header"
                >
                    <b-col cols="3">Item:</b-col>
                    <b-col cols="3">From:</b-col>
                    <b-col cols="3">Due Date:</b-col>
                    <b-col cols="3">Price:</b-col>
                </b-row>
                <b-row>
                    <b-col cols="3">
                        <p>Home Sale Legal Protection™</p>
                    </b-col>
                    <b-col cols="3">
                        {{ this.payFrom }}
                    </b-col>
                    <b-col cols="3">
                        At close
                    </b-col>
                    <b-col cols="3">
                        <p>${{ Number(this.price).toFixed(2) }}</p>
                    </b-col>
                </b-row>

                <hr/>
                <br/>
                <div class="notes">
                    <p>Please send a copy of this invoice with payment to:</p>
                    <p><b><u>Address</u></b><br/>
                        SELLERS SHIELD INC <br/>
                        PO BOX 676975 <br/>
                        DALLAS, TX 75267-6975
                    </p>
                    <p>
                        <b><u>Overnight Mail Address</u></b><br/>
                        SELLERS SHIELD INC <br/>
                        LOCKBOX NUMBER 676975 <br/>
                        1200 E CAMPBELL RD STE 108 <br />
                        RICHARDSON, TX 75081
                    </p>
                    <p class="warning-text pink-text"><strong>*Please note: Sellers Shield is not a home
                        warranty</strong></p>
                </div>
                <hr/>
                <br/>
                <div class="invoice-footer">
                    <p>Please contact us by email at <strong>accounting@sellersshield.com</strong> or by phone at
                        <strong>844-747-4435</strong> if you have any questions.<br/>Thank you for your business.</p>
                </div>
            </div>
        </full-screen-dialog>
    </div>
</template>

<script>
import './EscrowInvoice.scss';
import FrontEndApiService from "@services/frontend.api.service";
import {AGENT} from "@sellers-shield-home-protection/ss-core-js/src/AppResources/LuHslpUpsellPurchasedByTypeAppResource";
import dayjs from "dayjs";

export default {
    name: 'EscrowInvoice',
    data: () => ({
        loading: false,
        order: null,
        property: null,
        agent: null,
        primarySeller: null,
        invitation: null,
        hslp: null,
        sellers: null,
        broker: null,
        closingDate: null,
        stateName: null,
        payFrom: null,
        price: null,
        logo: {
            path: require('@assets/images/ss-logo-white-bg.png'),
        },
        navigationDisabled: true,
        propertyData: null,
    }),
    watch: {
        async order(orderAttr) {
            this.closingDate = orderAttr.closingDate !== null ? dayjs(orderAttr.closingDate).format('MMMM D, YYYY') : 'N/A';
            this.agent = orderAttr.agent?.attributes;
            this.hslp = orderAttr.homeSaleLegalProtection?.attributes;
            this.invitation = orderAttr.invitation?.attributes;
            this.primarySeller = this.getPrimarySeller(orderAttr.sellers);
            this.broker = orderAttr.brokerUuid !== null ? orderAttr.broker.attributes : null;
            this.property = orderAttr.property ? orderAttr.property.attributes : null;
            this.propertyData = await this.getProperty();
            this.payFrom = this.hslp.upsellPurchasedById === AGENT ? 'Agent commission' : 'Seller proceeds';
            this.price = this.hslp.upsellPrice > 0 ? this.hslp.upsellPrice : orderAttr.price;
        }
    },
    methods: {
        dayjs(date) {
            return dayjs(date);
        },
        async getProperty() {
            let response = '';
            if (this.property) {
                await this.getStateName(this.property.stateId);
                response += this.property.streetAddress + ', ';
                response += this.property.city + ', ';
                response += this.stateName + ', ';
                response += this.property.zip + ' ';
            } else {
                if (this.invitation) {
                    let property = JSON.parse(this.invitation.property);
                    await this.getStateName(property['state']);
                    response += property['address'] + ', ';
                    response += property['city'] + ', ';
                    response += this.stateName + ', ';
                    response += property['zip'] + ', ';
                }
            }
            return response;
        },
        async getStateName(stateId) {
            this.stateName = 'N/A';
            if (stateId > 0) {
                const {data: state} = await FrontEndApiService.post('public-api/v1/lus/states/search', {
                    "filter": {"andOr": "and", "params": {"id": stateId}},
                });
                if (state.data) {
                    this.stateName = state.data[0]['attributes'].title;
                }
            }
        },
        getPrimarySeller(sellers) {
            let returnName = '';
            if (sellers) {
                for (const [key, value] of Object.entries(sellers)) {
                    if (value['attributes'].type === "primary") {
                        returnName = value['attributes'].firstName + ' ' + value['attributes'].lastName;
                    }
                }
            } else {
                let primarySeller = JSON.parse(this.invitation?.primarySeller ?? {});
                returnName = !!primarySeller ? primarySeller['first_name'] + ' ' + primarySeller['last_name'] : '';
            }
            return returnName;
        },
        async fetchOrder() {
            try {
                const urlParams = new URLSearchParams(window.location.search);
                const orderUUid = urlParams.get('order-id');
                const token = urlParams.get('officer-token');
                FrontEndApiService.init(window.env.APP_URL);
                if (!!orderUUid) {
                    const {data: zohoOrder} = await FrontEndApiService.post('public-api/v1/orders/escrow/invoice/', {
                        "filter": {"andOr": "and", "params": {"uuid": orderUUid, "zoho_id": token}}
                    });
                    this.order = zohoOrder.data[0]['attributes'];
                } else {
                    const {data: order} = await FrontEndApiService.post('public-api/v1/orders/escrow/invoice/', {
                        "filter": {"andOr": "and", "params": {"escrowInvoiceToken": token}}
                    });
                    this.order = order.data[0]['attributes'];
                }
            } catch (error) {
                const response = error.response.data;
                const {status} = error.response;
                switch (status) {
                    case 302:
                        window.location.href = response.redirect;
                        return false;
                        break;
                }
            }
        },
        printInvoicePdf() {
            //logo
            window.onbeforeprint = () => {
                document.getElementsByClassName('logo')[0].style.display = 'initial';
                document.getElementsByClassName('escrow-invoice')[0].style.fontSize = '1.3rem';
                document.getElementsByClassName('pdf-hide')[0].style.display = 'none';
                document.getElementsByClassName('pdf-show')[0].style.display = 'initial';
                document.getElementsByClassName('invoice-footer')[0].style.textAlign = 'center';
                let leftCol = document.getElementsByClassName("col-4");
                for (let i = 0; i < leftCol.length; i++) {
                    leftCol[i].style.backgroundColor = "#E0E0E0";
                }
                let rightCol = document.getElementsByClassName("col-8");
                for (let i = 0; i < rightCol.length; i++) {
                    rightCol[i].style.backgroundColor = "#E0E0E0";
                }
            }
            window.onafterprint = () => {
                document.getElementsByClassName('logo')[0].style.display = 'none';
                document.getElementsByClassName('escrow-invoice')[0].style.fontSize = '0.875rem';
                document.getElementsByClassName('pdf-hide')[0].style.display = 'initial';
                document.getElementsByClassName('pdf-show')[0].style.display = 'none';
                document.getElementsByClassName('invoice-footer')[0].style.textAlign = 'left';
                let leftCol = document.getElementsByClassName("col-4");
                for (let i = 0; i < leftCol.length; i++) {
                    leftCol[i].style.backgroundColor = "#fff";
                }
                let rightCol = document.getElementsByClassName("col-8");
                for (let i = 0; i < rightCol.length; i++) {
                    rightCol[i].style.backgroundColor = "#fff";
                }
            }
            window.print();
        }
    },
    async created() {
        await this.fetchOrder();
    }
}
</script>
